import type { AccountFeatureFlagType } from 'containers/Studio/config/cbk';
import type { IHome, IStoreShape } from 'createStore';
import { Map } from 'immutable';
import isEmpty from 'lodash/isEmpty';
import { createSelector } from 'reselect';

/**
 * Direct selector to the homePage state domain
 */

const selectHomePageDomain = () => (state: Map<string, IStoreShape>) =>
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  state.get('home', new Map()) as unknown as Map<string, IHome>;

/**
 * Other specific selectors
 */

/**
 * Default selector used by HomePage
 */

const selectHomePage = () =>
  createSelector(selectHomePageDomain(), (substate) => substate.toJS());

const selectFullName = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) =>
      `${substate.getIn(['user', 'first'])} ${substate.getIn(['user', 'last'])}`
  );

const selectAccountId = () =>
  createSelector(selectHomePageDomain(), (substate) => {
    if (isEmpty(substate.toJS()) || !substate.get('accountId')) {
      return -1;
    }
    return substate.get('accountId') as unknown as IHome['accountId'];
  });

const selectUser = () =>
  createSelector(selectHomePageDomain(), (substate) => {
    const user = substate.get('user') as unknown as Map<string, IHome['user']>;
    return user.toJS() as IHome['user'];
  });

const selectUserId = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) =>
      substate.getIn(['user', 'id']) as unknown as IHome['user']['id']
  );

const selectLoading = () =>
  createSelector(selectHomePageDomain(), (substate) => substate.get('loading'));

const selectUserPermissions = () =>
  createSelector(selectHomePageDomain(), (substate) => {
    const permissions = substate.get('permissions') as unknown as Map<
      string,
      IHome['permissions']
    >;
    return permissions?.toJS() as IHome['permissions'];
  });

const selectIsPasswordAuth = () =>
  createSelector(selectHomePageDomain(), (substate) => {
    const authMethod = substate.get(
      'authMethod'
    ) as unknown as IHome['authMethod'];
    return authMethod === 'password';
  });

const selectUserRoleName = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) => substate.get('roleName') as unknown as IHome['roleName']
  );

const selectIsAIProviderConfigEnabled = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) =>
      substate.get('isAIProviderConfigEnabled') as unknown as boolean
  );

const selectIsEnterprise = () => {
  const selector = createSelector(
    selectHomePageDomain(),
    (substate) => substate.get('isEnterprise') as unknown as boolean
  );
  return selector;
};

const selectIsDatabaseAppEnabled = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) => substate.get('isDatabaseAppEnabled') as unknown as boolean
  );

const selectIsDocumentReviewEnabled = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) => substate.get('isDocumentReviewEnabled') as unknown as boolean
  );

const selectIsAppEmailSenderEnabled = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) => substate.get('isAppEmailSenderEnabled') as unknown as boolean
  );
const selectIsCheckboxBrandingOptional = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) =>
      substate.get('isCheckboxBrandingOptional') as unknown as boolean
  );

const selectIsSelfServeSSOEnabled = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) => substate.get('isSelfServeSSOEnabled') as unknown as boolean
  );

const selectIsAIEnabled = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) => substate.get('isAIEnabled') as unknown as boolean
  );

const selectAIAssistantAppLimit = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) => substate.get('aIAssistantAppLimit') as unknown as number
  );

const selectIsTicketingBulkSelectionEnabled = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) =>
      substate.get('isTicketingBulkSelectionEnabled') as unknown as boolean
  );

const selectIsTicketingFeatureEnabled = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) =>
      substate.get('isTicketingFeatureEnabled') as unknown as boolean
  );

const selectIsXakiaEnabled = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) => substate.get('isXakiaEnabled') as unknown as boolean
  );

const selectIsTriagingEnabled = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) => substate.get('isTriagingEnabled') as unknown as boolean
  );

const selectIsSharepointEnabled = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) => substate.get('isSharepointEnabled') as unknown as boolean
  );

const selectIsTermExtractionEnabled = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) => substate.get('isTermExtractionEnabled') as unknown as boolean
  );

const selectAccountFeatureFlag = (accountFeatureFlag: AccountFeatureFlagType) =>
  createSelector(
    selectHomePageDomain(),
    (substate) => substate.get(accountFeatureFlag) as unknown as boolean
  );
const selectIsSalesforceEnabled = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) => substate.get('isSalesforceEnabled') as unknown as boolean
  );

const selectIsAiAgentEnabled = () =>
  createSelector(
    selectHomePageDomain(),
    (substate) => substate.get('isAIAssistantEnabled') as unknown as boolean
  );

export default selectHomePage;
export {
  selectAccountId,
  selectFullName,
  selectHomePageDomain,
  selectIsAIEnabled,
  selectIsAppEmailSenderEnabled,
  selectIsCheckboxBrandingOptional,
  selectIsDatabaseAppEnabled,
  selectIsDocumentReviewEnabled,
  selectIsEnterprise,
  selectIsPasswordAuth,
  selectIsSelfServeSSOEnabled,
  selectLoading,
  selectUser,
  selectUserId,
  selectUserPermissions,
  selectUserRoleName,
  selectAIAssistantAppLimit,
  selectIsAIProviderConfigEnabled,
  selectIsTicketingBulkSelectionEnabled,
  selectIsTicketingFeatureEnabled,
  selectIsXakiaEnabled,
  selectIsTriagingEnabled,
  selectIsSharepointEnabled,
  selectIsTermExtractionEnabled,
  selectAccountFeatureFlag,
  selectIsSalesforceEnabled,
  selectIsAiAgentEnabled,
};
